import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  width?: 'sm' | 'md' | 'lg';
  // Override the default scrolling body wrapper — e.g. for a chat layout that
  // manages its own scroll region and a sticky composer.
  bodyClassName?: string;
  // Let the user drag the left edge to resize the drawer (desktop only; on
  // narrow screens it always fills the width). Width persists per storageKey.
  resizable?: boolean;
  storageKey?: string;
}

const widths: Record<NonNullable<DrawerProps['width']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

// Default desktop width (px) per size token when resizable, before the user drags.
const defaultPx: Record<NonNullable<DrawerProps['width']>, number> = {
  sm: 384,
  md: 448,
  lg: 512,
};

const MIN_WIDTH = 360;

export default function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  width = 'md',
  bodyClassName = 'flex-1 overflow-y-auto p-5',
  resizable = false,
  storageKey,
}: DrawerProps) {
  const [widthPx, setWidthPx] = useState<number>(() => {
    if (!resizable || typeof window === 'undefined') return defaultPx[width];
    const saved = storageKey ? Number(window.localStorage.getItem(storageKey)) : NaN;
    return Number.isFinite(saved) && saved >= MIN_WIDTH ? saved : defaultPx[width];
  });
  const dragging = useRef(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const startResize = useCallback(
    (e: React.PointerEvent) => {
      if (!resizable) return;
      e.preventDefault();
      dragging.current = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      const onMove = (ev: PointerEvent) => {
        if (!dragging.current) return;
        // Drawer is anchored right, so width grows as the pointer moves left.
        const next = window.innerWidth - ev.clientX;
        const max = Math.min(window.innerWidth * 0.95, 1200);
        setWidthPx(Math.max(MIN_WIDTH, Math.min(next, max)));
      };
      const onUp = () => {
        dragging.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (storageKey) {
          setWidthPx((w) => {
            window.localStorage.setItem(storageKey, String(Math.round(w)));
            return w;
          });
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [resizable, storageKey]
  );

  // When resizable, drive width via inline style on desktop; force full width on
  // narrow screens with `!w-full` (an important utility beats the inline style).
  const sizeClass = resizable ? 'w-full max-md:!w-full' : `w-full ${widths[width]}`;
  const sizeStyle = resizable ? { width: widthPx } : undefined;

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-[9998]"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            style={sizeStyle}
            className={`fixed top-0 right-0 h-full ${sizeClass} bg-white dark:bg-gray-900 shadow-2xl z-[9999] flex flex-col`}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            {resizable && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize"
                onPointerDown={startResize}
                onDoubleClick={() => {
                  setWidthPx(defaultPx[width]);
                  if (storageKey) window.localStorage.removeItem(storageKey);
                }}
                className="absolute left-0 top-0 z-10 hidden h-full w-1.5 -translate-x-1/2 cursor-col-resize bg-transparent transition-colors hover:bg-primary-400/50 md:block"
                title="Drag to resize · double-click to reset"
              />
            )}
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                {title && (
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p className="text-xs uppercase tracking-wide text-gray-500 mt-0.5 truncate">
                    {subtitle}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className={bodyClassName}>{children}</div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
